/*!
 * Copyright Optimus Ride 2020
 */

import {GRPC_PLACES_PORT} from '../../constants';
import Auth from '../../auth/abstract-auth';
import {PlacesServiceClient} from '../../grpc/PlacesServiceClientPb';
import * as grpcPlaces from '../../grpc/places_pb';
import { 
  PlaceResult, 
  toPlaceResult,
  AutocompletePrediction,
  toAutocompletePrediction,
  DirectionsRoute,
  toDirectionsRoute, 
} from '@optimusride/client-library/@types/grpc/places';
import {CONSOLE_ERROR_MSG, requestMetadata} from '../util';
import { OperatingArea } from '@optimusride/client-library/@types/grpc/site';
import { Station, toStation } from '@optimusride/client-library/@types/grpc/station';

interface PlaceDetailsResponse {
  opAreaId:number|undefined;
  result:PlaceResult|undefined;
  closestStation:Station|undefined;
}

function toPlaceDetailsResponse(res:grpcPlaces.PlaceDetailsResponse):PlaceDetailsResponse{
  let grpcResult = res.getPlaceResult()
  let grpcClosestStation = res.getClosestStation()

  if(typeof grpcResult !== 'undefined' && typeof grpcClosestStation !== 'undefined'){
    return(
      {
        opAreaId:res.getOpAreaId(),
        result:toPlaceResult(grpcResult),
        closestStation:toStation(grpcClosestStation)
      }
    )
  }else{
    return {
      opAreaId:undefined,
      result:undefined,
      closestStation:undefined
    }
  }
}

interface AutocompletePredictionsResponse {
  predictions:AutocompletePrediction[];
}

function toAutocompletePredictionsResponse(res:grpcPlaces.AutocompletePredictionsResponse):AutocompletePredictionsResponse{
  let grpcPredictions = res.getPredictionsList()

  if(typeof grpcPredictions !== 'undefined'){
    return(
      {
        predictions:grpcPredictions.map(p=>toAutocompletePrediction(p))
      }
    )
  }else{
    return {predictions:[]}
  }
}

export interface DirectionsResponse {
  routes: DirectionsRoute[];
}

export function toDirectionsResponse(res:grpcPlaces.DirectionsResponse): DirectionsResponse {
  return (
    {
      routes:res.getRoutesList().map(r=>toDirectionsRoute(r))
    }
  )
}

export default class PlacesServiceConsumer {
    private placesServiceClient: PlacesServiceClient;
    private auth: Auth;
    private oriApiKey: undefined | string;

    constructor(auth: Auth, hostname: string, oriApiKey?: undefined | string) {
        this.auth = auth;
        this.oriApiKey = oriApiKey;
        this.placesServiceClient = new PlacesServiceClient(`${hostname}:${GRPC_PLACES_PORT}`, null, null);
    }

  public getAutoCompletePredictions = (userEntry: string, selectedOperatingArea: OperatingArea, sessionToken:string): Promise<AutocompletePredictionsResponse|undefined> => {
    const r = new grpcPlaces.AutocompletePredictionsRequest();
    r.setEntry(userEntry);
    r.setOpAreaId(selectedOperatingArea.id);
    r.setSessionToken(sessionToken);
    r.setLanguage('en');
    r.setRadius(5000);

    return new Promise(async (resolve, reject) => {
      this.placesServiceClient.getAutocompletePredictions(
          r, await requestMetadata(this.auth, this.oriApiKey), (err, response) => {
        if (!err) {
          if (response.getPredictionsList()) {
            resolve(toAutocompletePredictionsResponse(response));
          } else {
            throw new Error(CONSOLE_ERROR_MSG);
          }
        } else {
          reject(err);
        }
      });
    });
  }

  public getPlaceDetailsFromLatLng = (lat: number, lng: number, selectedOperatingArea: OperatingArea): Promise<PlaceDetailsResponse | undefined> => {
    const r = new grpcPlaces.PlaceDetailsFromLatLngRequest();
    let grpcLatLng = new grpcPlaces.LatLngLiteral()
    grpcLatLng.setLat(lat)
    grpcLatLng.setLng(lng)
    r.setLatLng(grpcLatLng);
    r.setOpAreaId(selectedOperatingArea.id);

    return new Promise(async (resolve, reject) => {
      this.placesServiceClient.getPlaceDetailsFromLatLng(
          r, await requestMetadata(this.auth, this.oriApiKey), (err, response) => {
        if (!err) {
          if (response.getPlaceResult()) {
            resolve(toPlaceDetailsResponse(response))
          } else {
            throw new Error(CONSOLE_ERROR_MSG);
          }
        } else {
          reject(err);
        }
      });
    });
  }

  public getPlaceDetails = (placeId: string, selectedOperatingArea: OperatingArea): Promise<PlaceDetailsResponse | undefined> => {
    const r = new grpcPlaces.PlaceDetailsRequest();
    r.setPlaceId(placeId);
    r.setOpAreaId(selectedOperatingArea.id);
    r.setLanguage('en');

    return new Promise(async (resolve, reject) => {
      this.placesServiceClient.getPlaceDetails(
          r, await requestMetadata(this.auth, this.oriApiKey), (err, response) => {
        if (!err) {
          if (response.getPlaceResult()) {
            resolve(toPlaceDetailsResponse(response))
          } else {
            throw new Error(CONSOLE_ERROR_MSG);
          }
        } else {
          reject(err);
        }
      });
    });
  }

  public getDirections = (origin: string, destination:string): Promise<DirectionsResponse | undefined> => {
    const r = new grpcPlaces.DirectionsRequest();
    r.setOrigin(origin);
    r.setDestination(destination);

    return new Promise(async (resolve, reject) => {
      this.placesServiceClient.getDirections(
          r, await requestMetadata(this.auth, this.oriApiKey), (err, response) => {
        if (!err) {
          if (response.getRoutesList()) {
            let parsedResponse = toDirectionsResponse(response)
            resolve(parsedResponse);
          } else {
            throw new Error(CONSOLE_ERROR_MSG);
          }
        } else {
          reject(err);
        }
      });
    });
  }

}
